import type { Au13ReclaimResult } from "./reclaim.js";
import type { Au13Output, CcsCareType } from "./types.js";

// Plain-English CCS result copy (spec §5.2 pattern, §7.4–7.5 content). Lives in
// the rules package so every surface (Expo result screen, "my results" list,
// the reclaim-letter PDF) renders identical wording from one source. This is a
// regulatory requirement, not just DRY: AU-13 is information, not advice
// (validation §3, spec §4.1, §7.5). We STATE the entitlement, how it was
// derived, and the Services Australia source — with NO "you should claim", NO
// "you'll save $X", NO product/provider recommendation. CCS carries an extra
// guardrail: we are NOT a Services Australia agent and the reclaim output is a
// "personalised summary", never an approval. Pure string-builders, unit-tested.

/** Whole-dollar (or cents where < $10) AUD, en-AU formatting. */
export function formatAud(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return `A$${rounded.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

const CARE_TYPE_LABEL: Record<CcsCareType, string> = {
  centre_based_day_care: "centre-based day care",
  family_day_care: "family day care",
  outside_school_hours_care: "outside school hours care",
  in_home_care: "in-home care",
};

export function careTypeLabel(careType: CcsCareType): string {
  return CARE_TYPE_LABEL[careType];
}

/** Headline: the fortnightly subsidy as a figure, never as a saving. */
export function entitlementHeadline(output: Au13Output): string {
  if (output.subsidyPerFortnight === 0) {
    return "A$0 — no Child Care Subsidy applies on these figures";
  }
  return `Your Child Care Subsidy on these figures: ${formatAud(
    output.subsidyPerFortnight,
  )} per fortnight`;
}

/** How the percentage was derived — states the tier, no advice. */
export function subsidyPercentBullet(output: Au13Output): string {
  const base = `Your applicable subsidy percentage is ${pct(output.subsidyPercent)}, based on your family income tier.`;
  return output.higherRateApplied
    ? `${base} The higher rate for a second or younger child aged 5 or under has been applied.`
    : base;
}

/** How the subsidised hours were derived. */
export function hoursBullet(output: Au13Output): string {
  return `You are entitled to ${output.subsidisedHoursEntitlement} subsidised hours per fortnight; this calculation subsidises ${output.subsidisedHours} of the hours of care entered.`;
}

/** How the per-hour subsidy was derived (cap vs fee). */
export function rateBullet(output: Au13Output, careType: CcsCareType): string {
  const capped = output.subsidisedRate === output.cappedHourlyRate;
  const basis = capped
    ? `the ${careTypeLabel(careType)} hourly rate cap of ${formatAud(output.cappedHourlyRate)} (your fee is at or above the cap)`
    : `your hourly fee of ${formatAud(output.subsidisedRate)} (below the ${careTypeLabel(careType)} hourly rate cap of ${formatAud(output.cappedHourlyRate)})`;
  return `The subsidy is ${pct(output.subsidyPercent)} of ${basis}, which is ${formatAud(output.subsidyPerHour)} per subsidised hour.`;
}

/** Source-attribution line, composed from the period metadata in force. */
export function sourceBullet(periodLabel: string, verifiedAt: string): string {
  return `These figures use the Services Australia and Department of Education Child Care Subsidy rules for ${periodLabel} (last verified ${verifiedAt}). Rates, thresholds and hourly rate caps change each financial year — always check servicesaustralia.gov.au.`;
}

export function explanationBullets(
  output: Au13Output,
  careType: CcsCareType,
  periodLabel: string,
  verifiedAt: string,
): string[] {
  return [
    subsidyPercentBullet(output),
    hoursBullet(output),
    rateBullet(output, careType),
    sourceBullet(periodLabel, verifiedAt),
  ];
}

/**
 * Reclaim-letter framing (spec §7.5). MUST present the figure as a personalised
 * summary, NOT a Services Australia approval or form. The mandatory disclaimer
 * is returned alongside so every surface renders it.
 */
export const RECLAIM_DISCLAIMER =
  "This is a personalised summary of information for your own use. We are not authorised by Services Australia. Before submitting any reclaim, verify your eligibility at servicesaustralia.gov.au or call 136 150.";

export function reclaimHeadline(result: Au13ReclaimResult): string {
  return `Personalised Child Care Subsidy summary for ${result.periodLabel}: ${formatAud(
    result.output.subsidyPerFortnight,
  )} subsidy per fortnight on the figures you entered.`;
}

export function reclaimExplanation(result: Au13ReclaimResult): string[] {
  const reformNote = result.postCheaperChildCareReform
    ? "These figures use the Child Care Subsidy rules that applied after the 10 July 2023 changes."
    : "These figures use the Child Care Subsidy rules that applied before the 10 July 2023 changes.";
  return [
    reclaimHeadline(result),
    `This summary covers care on ${result.careDate}, which falls in ${result.periodLabel}.`,
    reformNote,
    RECLAIM_DISCLAIMER,
  ];
}
